import io from 'socket.io-client';
import moment from 'moment';

import * as actions from './actions';
import { startPageLoading, stopPageLoading } from '../page_details/actions';
import { resetReduxState } from '../admin_details/actions';
import { updateAgentMonitoringState } from '../agent_monitoring_details/actions';

import * as actionTypes from './actiontypes';
import { UPDATE_AGENT_STATE_LIST } from '../page_details/actiontypes';
import { CHANGE_AGENT_STATE, AGENT_STATE_UPDATED } from '../admin_details/actiontypes';
import { CONVERSATION_LOG_STATS_UPDATED } from '../usage_details/actiontypes';
import { AGENT_MONITORING_CUSTOMER_MESSAGE_RECEIVED, AGENT_MONITORING_MESSAGE_RESPONSE_RECEIVED } from '../agent_monitoring_details/actiontypes';
import { showMessage, getAuthSocketData, log } from '../../config/utils';
import {
  SOCKET_EVENTS,
  ROUTE_PATH,
  MESSAGE_READ_STATUS,
  CHAT_STATE,
  LOCAL_STORAGE,
  CHAT_LIST_TYPE
} from '../../config/constants';
import { BRAND_INFO, MODULE_CONFIG } from '../../config/urls';
import { sendPushNotification } from '../../notification/pushNotification'
import { joinChatRoom, shownJcrContent } from './api'

const registerSocketListener = (store, socket) => {
  const admin_id = store.getState().admin_details.admin_id;
  socket.on(SOCKET_EVENTS.CONNECT, () => {
    log('chats socket connected', socket);
    store.dispatch(actions.updateChatStateValue(socket.connected, "is_socket_connected"));
  });

  socket.on(SOCKET_EVENTS.CONNECT_ERROR, error => {
    log('chats socket connect error', error);
    store.dispatch(actions.updateChatStateValue(socket.connected, "is_socket_connected"));
  });

  socket.on(SOCKET_EVENTS.ERROR, error => {
    log('chats socket error', error);
    store.dispatch(actions.updateChatStateValue(socket.connected, "is_socket_connected"));
  });

  socket.on(SOCKET_EVENTS.DISCONNECT, reason => {
    if (reason === 'io server disconnect')
      socket.connect();
    store.dispatch(actions.updateChatStateValue(socket.connected, "is_socket_connected"));
  });

  socket.on(SOCKET_EVENTS.FORCE_LOGOUT, data => {
    localStorage.removeItem(LOCAL_STORAGE.ORI_ADMIN);
    localStorage.removeItem(LOCAL_STORAGE.LIVE_CHATS_UNREAD_MESSAGE_COUNT);
    store.dispatch(resetReduxState());
    if (data && data.message)
      showMessage('warning', data.message)
  });

  socket.on(SOCKET_EVENTS.DETAILED_CHAT_STATS, payload => {
    log('data', payload);
    if (payload) {
      store.dispatch({
        type: CONVERSATION_LOG_STATS_UPDATED,
        payload,
      })
    }
  });

  socket.on(SOCKET_EVENTS.TAG_ADDITION, payload => {
    log('tagAddition', payload);
    if (payload && payload.newUntaggedChat && payload.newUntaggedChat.sessionId) {
      store.dispatch({
        type: actionTypes.CHATS_ADDITION,
        payload: payload.newUntaggedChat,
        id: payload.newUntaggedChat.sessionId,
        key: 'untagged_chats',
      })
    }
  });

  socket.on(SOCKET_EVENTS.ADDITION, payload => {
    log('addition', payload);
    if (payload && payload.newLiveUser && payload.newLiveUser.psid) {
      if (payload.newLiveUser.chatState === CHAT_STATE.AGENT_HANDLING && payload.newLiveUser.agentId === admin_id && (document.visibilityState === 'hidden' || window.location.pathname !== ROUTE_PATH.CHATS || !document.hasFocus())) {
        const options = {
          body: "New customer added",
          icon: BRAND_INFO.new_customer_notification_icon_url,
        };
        sendPushNotification(options);
      }
      store.dispatch({
        type: actionTypes.CHATS_ADDITION,
        payload: payload.newLiveUser,
        id: payload.newLiveUser.psid,
        key: 'live_chats',
      })
      if(MODULE_CONFIG.GLOBAL.DIAGNOSE)
        socket.emit(SOCKET_EVENTS.DIAGNOSE, {
          type: SOCKET_EVENTS.ADDITION,
          data: {
            eventListenTimeStamp: new Date().getTime(),
            acceptedChatTimeStamp: payload.newLiveUser.acceptedChatTimeStamp,
            clientPsid: payload.newLiveUser.psid
          }
        })
    }
  });

  socket.on(SOCKET_EVENTS.UPDATION, payload => { //TODO: handle updation in case of agent monitoring
    log('chats updation', payload);
    if (payload && payload.changedChatDetails && payload.changedChatDetails.psid) {
      store.dispatch({
        type: actionTypes.CHATS_UPDATION,
        payload: payload.changedChatDetails,
        admin_id
      });
    } else if (payload && payload.changedUserProfileDetails && payload.changedUserProfileDetails.psid && payload.changedUserProfileDetails.userProfileDetails) {
      const selected_active_chat_id = store.getState().chats_details.selected_active_chat_id;
      if (selected_active_chat_id && selected_active_chat_id === payload.changedUserProfileDetails.psid)
        store.dispatch(actions.updateChatStateValue(payload.changedUserProfileDetails.userProfileDetails, `active_chats.${selected_active_chat_id}.profile`));
      const active_agent_chat_item = store.getState().agent_monitoring_details.selected_agent.active_chat.chat_item;
      if (active_agent_chat_item && active_agent_chat_item.psid === payload.changedUserProfileDetails.psid)
        store.dispatch(updateAgentMonitoringState(payload.changedUserProfileDetails.userProfileDetails, `selected_agent.active_chat.profile`));
    }
  });

  socket.on(SOCKET_EVENTS.DELETION, payload => {
    log('chats deletion', payload);
    if (payload && payload.disconnectedChatPsid)
      store.dispatch({
        type: actionTypes.CHATS_DELETION,
        payload: { psid: payload.disconnectedChatPsid },
        reason: 'active client has been either disconnected or force takeover by  supervisor'
      });
  });

  socket.on(SOCKET_EVENTS.USER_MESSAGE, payload => {
    log('user message listen', payload);
    const active_chats = store.getState().chats_details.active_chats;
    const active_agent_chat_item = store.getState().agent_monitoring_details.selected_agent.active_chat.chat_item;
    if (payload && payload.psid && active_chats[payload.psid]) {
      const message = {
        ...payload.message,
        NLPSnapshot: payload.NLPSnapshot ? payload.NLPSnapshot : null,
        prevNLPSnapshot: payload.prevNLPSnapshot ? payload.prevNLPSnapshot : null,
      };
      store.dispatch({
        type: actionTypes.ADD_NEW_MESSAGE,
        payload: { message, psid: payload.psid },
      });
    } else if (active_agent_chat_item && payload && payload.psid && payload.psid === active_agent_chat_item.psid) {
      store.dispatch({
        type: AGENT_MONITORING_CUSTOMER_MESSAGE_RECEIVED,
        payload,
      });
    }

    const selected_active_chat_id = store.getState().chats_details.selected_active_chat_id;
    const live_chat_item = store.getState().chats_details.live_chats[payload.psid];
    if (payload && payload.psid && payload.message && payload.message.chatlogId && live_chat_item && live_chat_item.chatState === CHAT_STATE.AGENT_HANDLING && live_chat_item.agentId === admin_id) {
      if (document.visibilityState === 'hidden' || window.location.pathname !== ROUTE_PATH.CHATS || !document.hasFocus()) {
        const options = {
          body: "New message",
          icon: BRAND_INFO.new_msg_notification_icon_url,
        };
        sendPushNotification(options);
      }
      if (!(selected_active_chat_id && selected_active_chat_id === live_chat_item.psid)) {
        store.dispatch({
          type: actionTypes.UPDATE_UNREAD_LIVE_MESSAGE_COUNT,
          payload
        });
      }
      const updated_msg_data = {
        readStatus: MESSAGE_READ_STATUS.DELIVERED,
        senderPsid: admin_id,
        clientPsid: payload.psid,
        chatlogId: payload.message.chatlogId,
        timestamp: new Date().getTime()
      };
      socket.emit(SOCKET_EVENTS.UPDATE_CUSTOMER_MESSAGE, updated_msg_data, err => {
        const location = store.getState().routing.location;
        if (!err && document.visibilityState === 'visible' && document.hasFocus() && location && location.pathname === ROUTE_PATH.CHATS && selected_active_chat_id === payload.psid && live_chat_item.chatState === CHAT_STATE.AGENT_HANDLING && live_chat_item.agentId === admin_id) {
          const payload = {
            clientPsid: selected_active_chat_id,
            senderPsid: admin_id,
          };
          socket.emit(SOCKET_EVENTS.MESSAGE_SEEN, payload);
        }
      });
    }
  });

  socket.on(SOCKET_EVENTS.RESPONSE, payload => {
    log('response event', payload);
    const active_chats = store.getState().chats_details.active_chats;
    const active_agent_chat_item = store.getState().agent_monitoring_details.selected_agent.active_chat.chat_item;
    if (payload && payload.psid && payload.result && payload.result.bot_messages && payload.result.bot_messages.length > 0) {
      const message = payload.result.bot_messages[0];
      if (payload.chatlogId)
        message.chatlogId = payload.chatlogId;
      if (active_chats[payload.psid]) {
        store.dispatch({
          type: actionTypes.ADD_NEW_MESSAGE,
          payload: { message, psid: payload.psid }
        });
      }
      if (active_agent_chat_item && active_agent_chat_item.psid === payload.psid) {
        store.dispatch({
          type: AGENT_MONITORING_MESSAGE_RESPONSE_RECEIVED,
          payload: { message }
        });
      }
    }
  });

  socket.on(SOCKET_EVENTS.TYPING_STATUS, payload => {
    if (payload && payload.typingInfo && payload.typingInfo.sender && payload.typingInfo.clientPsid && payload.typingInfo.senderPsid !== admin_id) {
      store.dispatch({
        type: actionTypes.UPDATE_TYPING_INFO,
        payload
      });
    }
  });

  socket.on(SOCKET_EVENTS.GET_AGENT_STATE, payload => {
    log('get agent state event res', payload);
    if (payload && payload.selectedState) {
      store.dispatch({
        type: AGENT_STATE_UPDATED,
        payload: payload.selectedState,
      });
    }
    if (payload && payload.agentStates) {
      store.dispatch({
        type: UPDATE_AGENT_STATE_LIST,
        payload: payload.agentStates,
      });
    }
  });

  socket.on(SOCKET_EVENTS.UPDATE_MESSAGE, data => {
    log('update message event :-', data);
    const selected_active_chat_id = store.getState().chats_details.selected_active_chat_id;
    if (data && data.updatedAdminMessage && data.updatedAdminMessage.clientPsid && data.updatedAdminMessage.changedValue && data.updatedAdminMessage.chatlogId && data.updatedAdminMessage.senderPsid !== admin_id) {
      store.dispatch({
        type: actionTypes.UPDATE_CHAT_MESSAGE,
        payload: {
          ...data.updatedAdminMessage,
          psid: data.updatedAdminMessage.clientPsid,
        }
      });
    } else if (selected_active_chat_id && data && data.messageSeen && data.messageSeen.clientPsid === selected_active_chat_id && data.messageSeen.senderPsid === selected_active_chat_id) {
      store.dispatch({
        type: actionTypes.CHATS_MESSAGE_SEEN,
        payload: data.messageSeen
      });
    }
  });
};

const middleware = () => {
  let socket = null;
  return store => next => action => {
    switch (action.type) {
      case actionTypes.CHATS_MAKE_SOCKET_CONNECTION: {
        if (socket !== null)
          socket.close();
        const AUTH_SOCKET_DATA = getAuthSocketData(action.ip);
        socket = io(action.host, AUTH_SOCKET_DATA);
        registerSocketListener(store, socket);
        break;
      }

      case actionTypes.CALL_CHATS_SOCKET_METHOD:
        if (socket)
          socket[action.payload]()
        break

      case actionTypes.JOIN_CHAT_ITEM_BY_PSID: {
        const chat_item = store.getState().chats_details.live_chats[action.payload];
        if (chat_item)
          store.dispatch(actions.chatItemClicked(chat_item, action.admin_id))
        break;
      }

      case actionTypes.EMIT_CHATS_CUSTOM_EVENT: {
        if (socket && action.event) {
          log(`emit ${action.event}`, action.payload);
          socket.emit(action.event, action.payload, (err, res) => {
            log('emit res', res);
            if (action.callback)
              action.callback(err, res);
            if (err && res && res.errorCode && res.errorCode === 204) {
              showMessage('error', res && res.message ? res.message : `${action.event} error occured`);
              store.dispatch(actions.joinChatsPage());
            }
          })
        }
        break;
      }

      case actionTypes.JOIN_CHATS_PAGE:
        if (socket) {
          let loaderCount = MODULE_CONFIG.CHATS.ALLOW_UNTAGGED_CHAT ? 2 : 1
          store.dispatch(actions.updateChatStateValue(true, "loaders.chat_page_joining"));
          socket.emit(SOCKET_EVENTS.JOIN, action.payload, (err, res) => {
            if (loaderCount === 2)
              loaderCount = loaderCount - 1
            else
              store.dispatch(actions.updateChatStateValue(false, "loaders.chat_page_joining"));
            if (!err && res && res.data && res.data.liveChatData) {
              store.dispatch({
                type: actionTypes.CHAT_LIST_RECEIVED_ON_JOIN,
                payload: res.data.liveChatData,
                key: 'live_chats'
              });
            } else
              showMessage('error', res && res.message ? res.message : "join event error occured");
          });
          if (MODULE_CONFIG.CHATS.ALLOW_UNTAGGED_CHAT) {
            socket.emit(SOCKET_EVENTS.JOIN_UNTAGGED_CHATS, action.payload, (err, res) => {
              log('joinUntaggedChats', err, res)
              if (loaderCount === 2)
                loaderCount = loaderCount - 1
              else
                store.dispatch(actions.updateChatStateValue(false, "loaders.chat_page_joining"));
              if (!err && res && res.data && res.data.UntaggedChatsData) {
                store.dispatch({
                  type: actionTypes.CHAT_LIST_RECEIVED_ON_JOIN,
                  payload: res.data.UntaggedChatsData,
                  key: "untagged_chats"
                });
              } else
                showMessage('error', res && res.message ? res.message : "joinUntagged event error occured");
            })
          }
        }
        break;

      case actionTypes.JOINED_ROOM_USER_PROFILE_RECEIVED:
        if (socket) {
          const selected_active_chat_id = store.getState().chats_details.selected_active_chat_id;
          if (selected_active_chat_id && selected_active_chat_id === action.payload.psid)
            store.dispatch(actions.updateChatStateValue(action.payload.userProfileDetails, `active_chats.${selected_active_chat_id}.profile`));
          const active_agent_chat_item = store.getState().agent_monitoring_details.selected_agent.active_chat.chat_item;
          if (active_agent_chat_item && active_agent_chat_item.psid === action.payload.psid)
            store.dispatch(updateAgentMonitoringState(action.payload.userProfileDetails, `selected_agent.active_chat.profile`));
        }
        break;

      case actionTypes.JOIN_CHAT_ROOM:
        if (socket) {
          store.dispatch(startPageLoading("joining room"));
          joinChatRoom({
            socketId: socket.id,
            clientPsid: action.payload.clientPsid,
            adminPsid: action.payload.adminPsid,
            timestamp: moment().valueOf()
          }, (err, res) => {
            store.dispatch(stopPageLoading());
            if (!err && res && res.data) {
              store.dispatch(actions.chatRoomJoined(res.data, action.payload.clientPsid));
              shownJcrContent({
                timestamp: moment().valueOf(),
                adminPsid: action.payload.adminPsid,
                sessionId: res.data.sessionId
              });
              store.dispatch(actions.getJoinedRoomUserProfile({ clientPsid: action.payload.clientPsid }))
              if (action.payload.chatState === CHAT_STATE.AGENT_HANDLING && action.payload.agentId === action.payload.adminPsid)
                socket.emit(SOCKET_EVENTS.MESSAGE_SEEN, {
                  clientPsid: action.payload.clientPsid,
                  senderPsid: action.payload.adminPsid
                });
            } else
              showMessage('error', 'unable to join chatroom');
          })
        }
        break;

      case actionTypes.CHAT_ITEM_CLICKED:
        if (socket) {
          const already_joined_chat_ids = Object.keys(store.getState().chats_details.active_chats)
          if (action.payload.type === CHAT_LIST_TYPE.UNTAGGED_CHATS) {
            const index = already_joined_chat_ids.findIndex(id => id === action.payload.id);
            if (index !== -1) {
              store.dispatch(actions.updateChatsState({
                selected_active_chat_id: action.payload.id,
                selected_active_chat_type: action.payload.type
              }))
            } else
              store.dispatch(actions.getUntaggedChatlogs(action.payload))
          } else {
            const index = already_joined_chat_ids.findIndex(id => id === action.payload.psid);
            if (action.payload.chatState === CHAT_STATE.AGENT_HANDLING && index !== -1) {
              store.dispatch(actions.switchAlreadyJoinedChat(action.payload.psid));
              if (action.payload.agentId === action.admin_id)
                socket.emit(SOCKET_EVENTS.MESSAGE_SEEN, { clientPsid: action.payload.psid, senderPsid: action.admin_id });
            } else
              store.dispatch(actions.joinChatRoom({ clientPsid: action.payload.psid, adminPsid: action.admin_id }));
          }
        }
        break;

      case actionTypes.EMIT_MESSAGE_SEEN:
        if (socket) {
          const selected_active_chat_id = store.getState().chats_details.selected_active_chat_id;
          if (selected_active_chat_id) {
            const admin_id = store.getState().admin_details.admin_id;
            const live_chat_item = store.getState().chats_details.live_chats[selected_active_chat_id];
            if (live_chat_item && live_chat_item.chatState === CHAT_STATE.AGENT_HANDLING && live_chat_item.agentId === admin_id)
              socket.emit(SOCKET_EVENTS.MESSAGE_SEEN, { clientPsid: selected_active_chat_id, senderPsid: admin_id });
          }
        }
        break;

      case CHANGE_AGENT_STATE:
        log('change agent state payload', action.payload);
        if (socket) {
          store.dispatch(startPageLoading("state updating"));
          socket.emit(SOCKET_EVENTS.CHANGE_AGENT_STATE, action.payload, (err, res) => {
            log('change agent state res', res);
            store.dispatch(stopPageLoading());
            if (!err && res && res.data && res.data.state) {
              store.dispatch({
                type: AGENT_STATE_UPDATED,
                payload: res.data,
              });
            } else
              showMessage('error', res && res.message ? res.message : "change agent state event error occured.");
          });
        }
        break;

      case actionTypes.CHATS_SOCKET_DISCONNECT:
        if (socket !== null) {
          log('chats socket disconnected');
          socket.close();
        }
        socket = null;
        break;

      default:
        return next(action);
    }
  };
};

export default middleware();



// WEBPACK FOOTER //
// ./src/data/redux/chats_details/middleware.js